import type { WalletConfig } from './connectors'

/**
 * How far above the latest base fee a transaction's fee cap sits.
 *
 * An EIP-1559 transaction pays the block's base fee plus its tip and never its
 * cap, and Arbitrum ignores the tip, so the headroom costs nothing: it only
 * decides how far the base fee may rise while the user reads the wallet's
 * popup before the node refuses the transaction.
 */
const BASE_FEE_HEADROOM = 2n

export interface FeeCaps {
  maxFeePerGas?: bigint
  maxPriorityFeePerGas?: bigint
}

/**
 * The fee cap every transaction this site asks a wallet to send carries.
 *
 * **Left to itself, a wallet can cap the fee at the base fee it last read.**
 * Staging's ENABLE TRADING failed on two consecutive steps (2026-09-26) with
 * `max fee per gas less than block base fee` — caps of 33,658,000 and
 * 33,904,000 wei against base fees of 33,894,000 and 34,196,000, a rise of
 * under 1% between the wallet's read and the send. viem hands a browser wallet
 * whatever fees the caller supplies and fills none of its own, so the only
 * place headroom can come from is here.
 *
 * **A failed read sends no cap**, and the wallet chooses as it did before: a
 * fee estimate is never a reason to refuse a transaction. The base fee is read
 * through the same transport the wallet config reads everything else through —
 * the wallet's own provider first — so it is the base fee of the chain the
 * wallet is about to send on.
 */
export async function feeCaps(config: WalletConfig): Promise<FeeCaps> {
  try {
    const { getBlock, estimateMaxPriorityFeePerGas } = await import('wagmi/actions')
    const [block, tip] = await Promise.all([
      getBlock(config),
      estimateMaxPriorityFeePerGas(config).catch(() => 0n),
    ])
    const base = block.baseFeePerGas
    if (typeof base !== 'bigint') return {}
    return { maxFeePerGas: base * BASE_FEE_HEADROOM + tip, maxPriorityFeePerGas: tip }
  } catch {
    return {}
  }
}
